import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ReportClosingParts } from '../closing/ReportClosingParts';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { monthLabel } from '../list/list-query';
import { RemoveAllowanceDialog } from '../flat-rate/RemoveAllowanceDialog';
import { isAllowanceLine } from '../flat-rate/category';
import { useAllowance } from '../flat-rate/use-allowance';
import { formatAmount } from '../money';
import { AddExpensesSheet } from './AddExpensesSheet';
import { DecisionFoot, DecisionMessages, FirstDecision, useRowAsk } from './ApproverParts';
import { NewReportDialog } from './NewReportDialog';
import { NoteFilesPanel } from './NoteFilesPanel';
import { ReportBar } from './ReportBar';
import { ReportParityHead } from './ReportParityHead';
import { ReportPurposeLines } from './ReportPurpose';
import { ReportExpenses } from './ReportExpenses';
import { ReportStatusBadge } from './ReportStatusBadge';
import type { Report, ReportExpenseRow } from './types';
import { useReport } from './use-report';
import { type ActionError, useReportActions } from './use-report-actions';
import { useDecisions } from './use-decisions';

/** A report's page (spec §1.3): the owner edits it while it is open, and reads it afterwards. */
export function ReportPage() {
  const { t } = useTranslation('expenses');
  const [editStale, setEditStale] = useState(false);
  const { reportId } = useParams();
  const { state, adopt, reload } = useReport(reportId);
  if (state.status === 'loading') {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('reports.page.loading')}
      </p>
    );
  }
  if (state.status !== 'ready') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Plus}
        title={t(state.status === 'missing' ? 'reports.page.missing' : 'reports.page.loadError')}
        action={
          <Button asChild variant="outline">
            <Link to="/expenses/reports">{t('reports.page.back')}</Link>
          </Button>
        }
      />
    );
  }
  return (
    <ReportView
      report={state.report}
      adopt={adopt}
      reload={reload}
      editStale={editStale}
      onEditStale={setEditStale}
    />
  );
}

function Totals({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  const cell = 'flex flex-col text-sm text-muted-foreground';
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-2" data-testid="report-totals">
      <span className={cell}>
        {t('reports.page.totals.total')}
        <b className="text-foreground tabular-nums">{formatAmount(report.total)}</b>
      </span>
      <span className={cell}>
        {t('reports.page.totals.reimbursable')}
        <b className="text-foreground tabular-nums">{formatAmount(report.reimbursable)}</b>
      </span>
      <span className={cell}>
        {t('reports.page.totals.company')}
        <b className="text-foreground tabular-nums">{formatAmount(report.companyPaid)}</b>
      </span>
    </div>
  );
}

function ReportHeader({ report }: { readonly report: Report }) {
  const { t, i18n } = useTranslation('expenses');
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">
          {t('reports.page.kind')}
        </span>
        <h2 id="report-title" className="screen-title">
          {report.number} · {report.title}
        </h2>
        <p className="screen-summary">
          {t('reports.page.period', {
            period: monthLabel(report.periodMonth, i18n.resolvedLanguage),
          })}
          {' · '}
          {t('reports.page.owner', { name: report.owner.displayName })}
        </p>
        <ReportPurposeLines report={report} />
      </div>
      <ReportStatusBadge status={report.status} stage={report.approvalStage} />
    </div>
  );
}

/** The approver's note on a report sent back (spec §1.3), above the table. */
function SentBackNote({ note }: { readonly note: string | null | undefined }) {
  const { t } = useTranslation('expenses');
  if (!note) return null;
  return (
    <div role="status" className="border-border bg-warning-tint rounded-lg border p-3 text-sm">
      <b>{t('reports.page.sentBackNote')}</b>
      <br />
      {note}
    </div>
  );
}

function ActionsError({ error }: { readonly error: ActionError | null }) {
  const { t } = useTranslation('expenses');
  if (!error) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {t(`reports.page.errors.${error}`)}
    </p>
  );
}

function AddButton({ onClick }: { readonly onClick: () => void }) {
  const { t } = useTranslation('expenses');
  return (
    <div>
      <Button type="button" variant="outline" onClick={onClick}>
        <Plus />
        {t('reports.page.add')}
      </Button>
    </div>
  );
}

function ReportView({
  report,
  adopt,
  reload,
  editStale,
  onEditStale,
}: {
  readonly report: Report;
  readonly adopt: (report: Report) => void;
  readonly reload: () => void;
  readonly editStale: boolean;
  readonly onEditStale: (stale: boolean) => void;
}) {
  const { t } = useTranslation('expenses');
  const editButton = useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const actions = useReportActions(report, adopt, reload);
  const allowance = useAllowance(report, adopt, reload);
  const decisions = useDecisions(report, adopt, reload);
  const rowAsk = useRowAsk(decisions.busy, decisions.decideRow);
  // No row actions on the caller's own report (`can.owned`), whatever the rows' own `can` says.
  const deciding =
    report.can?.owned !== true &&
    (report.can?.rejectExpense === true || report.can?.makeReimbursable === true);
  const editable = report.can?.editExpenses === true;
  const rows = report.expenses ?? [];
  // A flat-rate line leaves its note through its own route, with a reason (decision 203).
  const [removing, setRemoving] = useState<ReportExpenseRow | null>(null);
  const remove = (id: string): void => {
    const row = rows.find((candidate) => candidate.id === id);
    if (row && isAllowanceLine(row)) {
      allowance.dismiss();
      setRemoving(row);
    } else void actions.remove(id);
  };
  const note = report.status === 'more_info_requested' ? report.notes : null;

  return (
    <section aria-labelledby="report-title" className="flex flex-col gap-4">
      <Link to="/expenses/reports" className="text-muted-foreground text-sm hover:underline">
        {t('reports.page.back')}
      </Link>
      <ReportHeader report={report} />
      <ReportParityHead report={report} />
      {editStale ? (
        <p role="alert" className="text-destructive text-sm">
          {t('reports.edit.stale')}
        </p>
      ) : null}
      {(report.status === 'draft' || report.status === 'more_info_requested') &&
      report.can?.edit === true ? (
        <div>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            ref={editButton}
            onClick={() => {
              onEditStale(false);
              setEditing(true);
            }}
          >
            {t('reports.edit.title')}
          </Button>
        </div>
      ) : null}
      {editing ? (
        <NewReportDialog
          open
          report={report}
          onReturnFocus={() => editButton.current?.focus()}
          period={report.periodMonth}
          ownerUserId={report.owner.userId}
          onClose={() => setEditing(false)}
          onCreated={(updated) => {
            adopt(updated);
            setEditing(false);
          }}
          onStale={() => {
            setEditing(false);
            onEditStale(true);
            reload();
          }}
        />
      ) : null}
      <SentBackNote note={note} />
      <FirstDecision report={report} />
      <DecisionMessages error={decisions.error} done={decisions.done} />
      <ActionsError error={actions.error} />
      {/* #915 W1: the note's own files beside its expenses from lg up (U1 board 1.4). */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-start">
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('reports.page.noExpenses')}</p>
        ) : (
          <ReportExpenses
            rows={rows}
            editable={editable}
            removesAllowance={allowance.removes}
            busy={actions.busy || decisions.busy || allowance.busy}
            onRemove={remove}
            {...(deciding ? { onDecide: rowAsk.open } : {})}
          />
        )}
        <NoteFilesPanel report={report} onMoved={reload} />
      </div>
      {editable ? <AddButton onClick={() => setAdding(true)} /> : null}
      {removing ? (
        <RemoveAllowanceDialog
          row={removing}
          busy={allowance.busy}
          error={allowance.error}
          onClose={() => {
            allowance.dismiss();
            setRemoving(null);
          }}
          onConfirm={(reason) =>
            // Closed only once the line went: a refusal keeps the dialog and the reason typed.
            void allowance.remove(removing.id, reason).then((gone) => {
              if (gone) setRemoving(null);
            })
          }
        />
      ) : null}
      <Totals report={report} />
      <ReportClosingParts report={report} adopt={adopt} />
      <DecisionFoot
        report={report}
        busy={decisions.busy}
        onDecide={(kind, note) => void decisions.decide(kind, note)}
      />
      {rowAsk.sheet}
      <ReportBar
        report={report}
        busy={actions.busy}
        onSubmit={() => void actions.submit()}
        onDiscard={() => void actions.discard()}
      />
      {editable ? (
        <AddExpensesSheet
          open={adding}
          reportId={report.id}
          number={report.number}
          period={report.periodMonth}
          noteType={report.noteType ?? null}
          ownerId={report.owner.userId}
          busy={actions.busy}
          onClose={() => setAdding(false)}
          onAdd={(ids) => void actions.add(ids).then(() => setAdding(false))}
        />
      ) : null}
    </section>
  );
}
